export type PendenciasListFilters = {
  statusBusca: number;
  filtroEstagiario?: number;
  filtroResponsavel?: string;
};

export const PENDENCIAS_FILTER_STORAGE_KEY = 'sgi.folhas.pendencias.filters';

export const PENDENCIAS_FILTER_PARAM = {
  status: 'status',
  estagiario: 'estagiario',
  responsavel: 'responsavel',
} as const;

const EMPTY_FILTERS: PendenciasListFilters = { statusBusca: 0 };

type FilterStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function getSessionStorageSafe(): FilterStorage | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

function parsePositiveInt(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function normalizeFilters(filters: PendenciasListFilters): PendenciasListFilters {
  const statusBusca = Number.isFinite(filters.statusBusca) ? filters.statusBusca : 0;
  const filtroEstagiario = filters.filtroEstagiario && filters.filtroEstagiario > 0
    ? filters.filtroEstagiario
    : undefined;
  const filtroResponsavel =
    filters.filtroResponsavel && filters.filtroResponsavel !== '0'
      ? filters.filtroResponsavel
      : undefined;
  return { statusBusca, filtroEstagiario, filtroResponsavel };
}

export function pendenciasFiltersAreMeaningful(filters: PendenciasListFilters): boolean {
  const n = normalizeFilters(filters);
  return n.statusBusca !== 0 || n.filtroEstagiario != null || n.filtroResponsavel != null;
}

export function searchParamsHavePendenciasFilters(searchParams: URLSearchParams): boolean {
  return (
    searchParams.has(PENDENCIAS_FILTER_PARAM.status) ||
    searchParams.has(PENDENCIAS_FILTER_PARAM.estagiario) ||
    searchParams.has(PENDENCIAS_FILTER_PARAM.responsavel)
  );
}

export function readPendenciasFiltersFromSearchParams(
  searchParams: URLSearchParams,
): PendenciasListFilters {
  const statusRaw = searchParams.get(PENDENCIAS_FILTER_PARAM.status);
  const statusBusca = statusRaw == null || statusRaw === '' ? 0 : Number(statusRaw);
  return normalizeFilters({
    statusBusca: Number.isFinite(statusBusca) ? statusBusca : 0,
    filtroEstagiario: parsePositiveInt(searchParams.get(PENDENCIAS_FILTER_PARAM.estagiario)),
    filtroResponsavel: searchParams.get(PENDENCIAS_FILTER_PARAM.responsavel) ?? undefined,
  });
}

export function applyPendenciasFiltersToSearchParams(
  prev: URLSearchParams,
  filters: PendenciasListFilters,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  const n = normalizeFilters(filters);

  if (n.statusBusca !== 0) next.set(PENDENCIAS_FILTER_PARAM.status, String(n.statusBusca));
  else next.delete(PENDENCIAS_FILTER_PARAM.status);

  if (n.filtroEstagiario != null) {
    next.set(PENDENCIAS_FILTER_PARAM.estagiario, String(n.filtroEstagiario));
  } else {
    next.delete(PENDENCIAS_FILTER_PARAM.estagiario);
  }

  if (n.filtroResponsavel) next.set(PENDENCIAS_FILTER_PARAM.responsavel, n.filtroResponsavel);
  else next.delete(PENDENCIAS_FILTER_PARAM.responsavel);

  return next;
}

export function readPendenciasFiltersFromSession(
  storage: FilterStorage | null = getSessionStorageSafe(),
): PendenciasListFilters | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(PENDENCIAS_FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendenciasListFilters>;
    return normalizeFilters({
      statusBusca: typeof parsed.statusBusca === 'number' ? parsed.statusBusca : 0,
      filtroEstagiario:
        typeof parsed.filtroEstagiario === 'number' ? parsed.filtroEstagiario : undefined,
      filtroResponsavel:
        typeof parsed.filtroResponsavel === 'string' ? parsed.filtroResponsavel : undefined,
    });
  } catch {
    return null;
  }
}

export function writePendenciasFiltersToSession(
  filters: PendenciasListFilters,
  storage: FilterStorage | null = getSessionStorageSafe(),
): void {
  if (!storage) return;
  try {
    const n = normalizeFilters(filters);
    if (!pendenciasFiltersAreMeaningful(n)) {
      storage.removeItem(PENDENCIAS_FILTER_STORAGE_KEY);
      return;
    }
    storage.setItem(PENDENCIAS_FILTER_STORAGE_KEY, JSON.stringify(n));
  } catch {
    // quota / modo privado
  }
}

/** URL tem prioridade; se só houver `page`, recupera o último filtro da sessão. */
export function resolvePendenciasFiltersOnLoad(
  searchParams: URLSearchParams,
  storage: FilterStorage | null = getSessionStorageSafe(),
): PendenciasListFilters {
  if (searchParamsHavePendenciasFilters(searchParams)) {
    const fromUrl = readPendenciasFiltersFromSearchParams(searchParams);
    writePendenciasFiltersToSession(fromUrl, storage);
    return fromUrl;
  }
  return readPendenciasFiltersFromSession(storage) ?? EMPTY_FILTERS;
}
